import { useEffect, useMemo, useRef, useState } from 'react';

import { BAND } from '../report/constants.js';

/**
 * Choropleth of Irish counties — a port of app/public/map.js.
 *
 * Boundaries come from Natural Earth 10m admin-1, pre-projected to SVG paths
 * by app/scripts/build-map.mjs (26 counties, the question bank's own set).
 * Band thresholds arrive from the API rather than being redefined here, so
 * a county average is banded by the same rules as a company score.
 *
 * Hover and pin follow legacy's event rules exactly rather than a tidier
 * derived "pinned ?? hovered": entering a county activates it only while
 * nothing is pinned, and un-pinning clears the panel to "All counties" even
 * though the pointer is still over the county, until the next mouseenter.
 */

const BAND_ORDER = ['Foundation', 'Developing', 'Established', 'Advanced'];
const NAME_AREA_THRESHOLD = 0.2;
/** Hover and screen-reader detail beside a whole-percentage average; as in Admin.jsx. */
const oneDecimal = (precise) => `${precise.toFixed(1)}% to one decimal place`;

let geography = null;

/** Loaded once, then cached for the life of the page. */
async function loadGeography() {
  if (!geography) {
    const response = await fetch('/assets/ireland-counties.json');
    geography = await response.json();
  }
  return geography;
}

/** Band for a score, using the thresholds the server sent. */
function bandFor(score, thresholds) {
  if (score === null || score === undefined) return null;
  return (thresholds.find((b) => score >= b.min) ?? thresholds[thresholds.length - 1]).band;
}

/**
 * Per-county aggregate: counts and band breakdown here; the average and its
 * percentage come from the server (`averages`), which computes them over the
 * same companies with the dashboard's conversion.
 */
function summarise(companies, thresholds, averages) {
  const byCounty = new Map();
  for (const company of companies) {
    const key = company.county;
    if (!key) continue;
    if (!byCounty.has(key)) {
      byCounty.set(key, {
        county: key, total: 0, scored: 0, flags: 0,
        bands: Object.fromEntries(BAND_ORDER.map((b) => [b, 0])),
        unassessed: 0,
        companies: [],
      });
    }
    const entry = byCounty.get(key);
    entry.companies.push(company);
    entry.total += 1;
    entry.flags += company.riskFlags ?? 0;
    if (company.overall === null || company.overallBand === null) {
      entry.unassessed += 1;
    } else {
      entry.scored += 1;
      entry.bands[company.overallBand] += 1;
    }
  }
  for (const entry of byCounty.values()) {
    const server = averages?.byCounty?.[entry.county] ?? null;
    entry.average = server?.average ?? null;
    entry.percent = server?.percent ?? null;
    entry.percentPrecise = server?.percentPrecise ?? null;
    entry.band = bandFor(entry.average, thresholds);
  }
  return byCounty;
}

const emptyEntry = (name) => ({
  county: name, total: 0, scored: 0, average: null, percent: null, percentPrecise: null, band: null, flags: 0,
  bands: Object.fromEntries(BAND_ORDER.map((b) => [b, 0])), unassessed: 0,
});

const SmallPill = ({ band }) => (
  <span className="band-pill band-pill-sm" style={{ background: BAND[band].fill, color: BAND[band].on }}>{band}</span>
);

/* ── Detail panel ────────────────────────────────────────────────── */

function Breakdown({ entry }) {
  return (
    <div className="map-breakdown">
      {BAND_ORDER.map((band) => (
        <div key={band} className={`map-band-row${entry.bands[band] ? '' : ' empty'}`}>
          <span className="map-swatch" style={{ background: BAND[band].chart }} />
          <span className="map-band-name">{band}</span>
          <span className="map-band-count">{String(entry.bands[band])}</span>
        </div>
      ))}
      {entry.unassessed > 0 && (
        <div className="map-band-row">
          <span className="map-swatch" style={{ background: 'var(--line-strong)' }} />
          <span className="map-band-name">Not yet assessed</span>
          <span className="map-band-count">{String(entry.unassessed)}</span>
        </div>
      )}
    </div>
  );
}

function Detail({ entry, national, onOpen, detailRef }) {
  if (!entry) {
    return (
      <div className="map-detail" ref={detailRef}>
        <p className="map-detail-eyebrow">All counties</p>
        <h3 className="map-detail-title">{`${national.counties} counties represented`}</h3>
        <p className="map-detail-meta">{`${national.total} companies · ${national.scored} assessed`}</p>
        {national.average !== null && (
          <div className="map-detail-score">
            <span className="map-avg-value" title={oneDecimal(national.percentPrecise)}>{`${national.percent}%`}</span>
            <SmallPill band={national.band} />
          </div>
        )}
        <Breakdown entry={national} />
        <p className="map-hint">Hover a county to see its breakdown. Click to pin it.</p>
      </div>
    );
  }

  return (
    <div className="map-detail" ref={detailRef}>
      <p className="map-detail-eyebrow">County</p>
      <h3 className="map-detail-title">{entry.county}</h3>
      <p className="map-detail-meta">
        {`${entry.total} ${entry.total === 1 ? 'company' : 'companies'}`
          + (entry.scored === entry.total ? '' : ` · ${entry.scored} assessed`)}
      </p>
      {entry.average !== null ? (
        <>
          <div className="map-detail-score">
            <span className="map-avg-value" title={oneDecimal(entry.percentPrecise)}>{`${entry.percent}%`}</span>
            <SmallPill band={entry.band} />
          </div>
          <p className="map-detail-note">Average of assessed companies in this county</p>
        </>
      ) : (
        <p className="map-detail-note">No completed assessments yet</p>
      )}
      <Breakdown entry={entry} />
      {entry.flags > 0 && (
        <p className="map-detail-flags">{`${entry.flags} priority support ${entry.flags === 1 ? 'flag' : 'flags'}`}</p>
      )}
      {/* Drill-through, matching the other admin views. The server re-checks
          the staff session on every request; this is navigation, not access. */}
      {entry.companies?.length > 0 && onOpen && (
        <div className="map-companies">
          <p className="map-companies-head">Companies</p>
          {[...entry.companies].sort((a, b) => a.companyName.localeCompare(b.companyName)).map((company) => (
            company.overallBand ? (
              <div
                key={company.companyId}
                className="map-company openable"
                role="button"
                tabIndex={0}
                title={`Open ${company.companyName}'s report`}
                onClick={() => onOpen(company)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(company); }
                }}
              >
                <span className="map-company-name">{company.companyName}</span>
                <SmallPill band={company.overallBand} />
              </div>
            ) : (
              <div key={company.companyId} className="map-company">
                <span className="map-company-name">{company.companyName}</span>
                <span className="admin-status">Not assessed</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Legend ──────────────────────────────────────────────────────── */

function Legend({ byCounty }) {
  return (
    <div className="map-legend">
      {BAND_ORDER.map((band) => (
        <span key={band} className="map-legend-item">
          <span className="map-swatch" style={{ background: BAND[band].chart, borderColor: BAND[band].edge }} />
          <span>{`${band} (${[...byCounty.values()].filter((c) => c.band === band).length})`}</span>
        </span>
      ))}
      <span className="map-legend-item">
        <span className="map-swatch" style={{ background: 'var(--page-bg)', borderColor: 'var(--line-strong)' }} />
        <span>No companies</span>
      </span>
    </div>
  );
}

/* ── Map ─────────────────────────────────────────────────────────── */

export default function MapView({ companies, thresholds, averages, onOpenCompany }) {
  const [geo, setGeo] = useState(geography);
  const [failed, setFailed] = useState(null);
  const [active, setActive] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [tip, setTip] = useState(null);
  const figureRef = useRef(null);
  const detailRef = useRef(null);
  const pinnedRef = useRef(null);
  const revealRef = useRef(false);

  // The map loads a 110KB boundary asset, so it renders asynchronously into
  // a placeholder rather than blocking the tab switch.
  useEffect(() => {
    if (geo) return;
    loadGeography().then(setGeo, (error) => setFailed(error.message));
  }, [geo]);

  /**
   * Bring the panel into view if a pin lands it off-screen. After the render
   * that shows the pinned county, so the measurement is of the new panel;
   * an effect rather than requestAnimationFrame, which never fires in a
   * background tab.
   */
  useEffect(() => {
    if (!revealRef.current) return;
    revealRef.current = false;
    const box = detailRef.current?.getBoundingClientRect();
    if (box && (box.top < 80 || box.bottom > window.innerHeight)) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [pinned]);

  const byCounty = useMemo(() => summarise(companies, thresholds, averages), [companies, thresholds, averages]);
  const national = useMemo(() => {
    const scoredAll = companies.filter((c) => c.overall !== null);
    const n = {
      counties: byCounty.size,
      total: companies.length,
      scored: scoredAll.length,
      flags: companies.reduce((a, c) => a + (c.riskFlags ?? 0), 0),
      unassessed: companies.length - scoredAll.length,
      bands: Object.fromEntries(BAND_ORDER.map((b) => [b, companies.filter((c) => c.overallBand === b).length])),
      average: averages?.national?.average ?? null,
      percent: averages?.national?.percent ?? null,
      percentPrecise: averages?.national?.percentPrecise ?? null,
    };
    n.band = bandFor(n.average, thresholds);
    return n;
  }, [companies, byCounty, thresholds, averages]);

  if (failed) return <div className="map-host"><p className="login-error">{`Could not load the map: ${failed}`}</p></div>;
  if (!geo) return <div className="map-host"><p className="map-loading">Loading map…</p></div>;

  // Cursor tooltip — immediate identification without waiting for the eye
  // to travel to the side panel.
  const showTooltip = (name, entry, event) => {
    const box = figureRef.current.getBoundingClientRect();
    setTip({ name, entry, left: event.clientX - box.left + 14, top: event.clientY - box.top + 14 });
  };
  const hideTooltip = () => setTip((prev) => (prev ? { ...prev, hidden: true } : prev));

  const activeEntry = active ? byCounty.get(active) ?? emptyEntry(active) : null;

  return (
    <div className="map-host">
      <div className="map-layout">
        <div className="map-figure" ref={figureRef}>
          <svg
            viewBox={geo.viewBox}
            className={`map-svg${pinned ? ' pinned' : ''}`}
            role="img"
            aria-label="Map of Ireland shaded by average digital maturity per county"
          >
            {/* Outside the pilot, but the island reads wrong without it. */}
            <path d={geo.northernIreland} className="map-outside" />

            {geo.counties.map((county) => {
              const entry = byCounty.get(county.name);
              const enter = (event) => {
                showTooltip(county.name, entry, event);
                if (!pinnedRef.current) setActive(county.name);
              };
              const leave = () => {
                hideTooltip();
                if (!pinnedRef.current) setActive(null);
              };
              const click = () => {
                const next = pinnedRef.current === county.name ? null : county.name;
                pinnedRef.current = next;
                setPinned(next);
                setActive(next);
                revealRef.current = Boolean(next);
              };
              return (
                <path
                  key={county.name}
                  d={county.d}
                  className={`map-county${active === county.name ? ' active' : ''}`}
                  data-county={county.name}
                  tabIndex={0}
                  role="button"
                  aria-label={entry
                    ? `${county.name}: ${entry.total} companies, average ${entry.percent === null ? 'not assessed' : `${entry.percent}% (${oneDecimal(entry.percentPrecise)})`}`
                    : `${county.name}: no companies`}
                  fill={entry?.band ? BAND[entry.band].chart : undefined}
                  stroke={entry?.band ? BAND[entry.band].edge : undefined}
                  onMouseMove={(event) => showTooltip(county.name, entry, event)}
                  onMouseEnter={enter}
                  onMouseLeave={leave}
                  // A focus event has no pointer position, so the tooltip's
                  // left/top come out as NaNpx -- which the browser ignores,
                  // leaving it where it last was. Legacy did exactly this;
                  // kept for parity.
                  onFocus={enter}
                  onBlur={leave}
                  onClick={click}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); click(); }
                  }}
                >
                  {/* Native tooltip, so the county is identifiable even before
                      the panel updates and without any JavaScript at all. */}
                  <title>
                    {entry
                      ? `${county.name} — ${entry.total} ${entry.total === 1 ? 'company' : 'companies'}`
                        + (entry.percent === null ? '' : `, average ${entry.percent}% (${entry.band}; ${oneDecimal(entry.percentPrecise)})`)
                      : `${county.name} — no companies`}
                  </title>
                </path>
              );
            })}

            {/* Labels last so they sit above every fill. Small counties cannot
                carry a name at a readable size — Dublin is 6% of Cork's area —
                so below the threshold they show the count only. */}
            {geo.counties.map((county) => {
              const entry = byCounty.get(county.name);
              const [x, y] = county.label;
              const roomForName = county.area >= NAME_AREA_THRESHOLD;
              if (!roomForName && !entry) return null;
              return (
                <g key={`label-${county.name}`} className="map-label-group" pointerEvents="none">
                  {roomForName && (
                    <text x={x} y={entry ? y - 7 : y} className="map-label-name" textAnchor="middle">{county.name}</text>
                  )}
                  {entry && (
                    <text x={x} y={roomForName ? y + 11 : y + 4} className="map-label-count" textAnchor="middle">{String(entry.total)}</text>
                  )}
                </g>
              );
            })}
          </svg>

          <div
            className="map-tooltip"
            hidden={!tip || tip.hidden}
            style={tip ? { left: `${tip.left}px`, top: `${tip.top}px` } : undefined}
          >
            {tip && (
              <>
                <span className="map-tooltip-name">{tip.name}</span>
                <span className="map-tooltip-meta">
                  {tip.entry
                    ? `${tip.entry.total} ${tip.entry.total === 1 ? 'company' : 'companies'}`
                      + (tip.entry.average === null ? '' : ` · ${tip.entry.band}`)
                    : 'No companies'}
                </span>
              </>
            )}
          </div>

          <Legend byCounty={byCounty} />
        </div>

        <Detail entry={activeEntry} national={national} onOpen={onOpenCompany} detailRef={detailRef} />
      </div>

      <p className="map-source">
        {'Counties shaded by the average overall score of assessed companies. '
          + 'Numbers show how many companies sit in each county. '
          + 'Northern Ireland is shown unshaded — outside the pilot. '
          + 'Boundaries: Natural Earth 10m admin-1 (public domain).'}
      </p>
    </div>
  );
}
